import { useContext, useEffect, useState } from 'react';
import { getHostInfo } from '../state/index.js';
import { useMemoryData } from '../storage/index.js';
import { isBrowser } from '../utils/index.js';
import { DataContainer, IsomorphicDataContext, } from './isomorphic-data-context.js';
const pendingFetches = useMemoryData('isomorphic-pending-fetches', () => new Map());
const prefetchData = useMemoryData('isomorphic-prefetch-data', () => new Map());
export function useIsomorphicData(config, fetcher, initialData = () => undefined) {
    const dataContext = useContext(IsomorphicDataContext);
    const [pending, setPending] = useState(false);
    const [info, setInfo] = useState(() => config.info);
    const [data, setData] = useState(() => {
        if (getFetchedContent(config.id)?.data) {
            return getFetchedContent(config.id)?.data;
        }
        if (!dataContext) {
            return initialData();
        }
        const cache = dataContext.cache.get(config.id);
        if (cache) {
            return cache.data;
        }
        const fetchers = dataContext.fetchers;
        if (fetchers && config.type !== 'clientOnly') {
            fetchers.push({
                config: config,
                fetcher: fetcher,
            });
        }
    });
    const [error, setError] = useState(() => {
        if (!dataContext) {
            const content = getFetchedContent(config.id);
            return content?.error;
        }
        const cache = dataContext.cache.get(config.id);
        if (cache) {
            return cache.error;
        }
    });
    useEffect(() => {
        if (!data) {
            setPending(true);
            const pending = pendingFetches().get(config.id);
            const promise = pending || fetcher(info);
            promise
                .then(setData)
                .catch(setError)
                .finally(() => {
                setPending(false);
                pendingFetches().delete(config.id);
            });
            if (!pending) {
                pendingFetches().set(config.id, promise);
            }
        }
    }, []);
    async function refetch(info) {
        setPending(true);
        return fetcher(info, data)
            .then(data => {
            setData(data);
            setInfo(info);
        })
            .catch(setError)
            .finally(() => setPending(false));
    }
    return {
        pending,
        data,
        error,
        info,
        refetch,
    };
}
export function getIsomorphicDataCache() {
    return prefetchData();
}
export function setIsomorphicData(id, data) {
    prefetchData().set(id, data);
}
export function hasIsomorphicData(id) {
    return !!getFetchedContent(id);
}
function getFetchedContent(id) {
    if (prefetchData().has(id)) {
        return prefetchData().get(id);
    }
    const dataElem = isBrowser() && new DataContainer(id).query();
    if (!dataElem) {
        return;
    }
    try {
        return JSON.parse(dataElem.text);
    }
    catch (err) {
        if (getHostInfo().context.env !== 'prod') {
            // eslint-disable-next-line no-console
            console.log(err);
        }
    }
}
//# sourceMappingURL=isomorphic-data-hook.js.map